import React, { useState, useEffect, useRef, useMemo } from 'react';
import { NewsItem, NewsCategory, Business, SiteSettings } from '../types';
import { Search, Filter, X, TrendingUp, Clock, Tag, ArrowRight, ChevronDown, Zap, Briefcase, Store } from 'lucide-react';

interface AdvancedSearchProps {
  items: NewsItem[];
  categories: NewsCategory[]; 
  isOpen?: boolean;
  onClose?: () => void;
  onNavigate?: (item: NewsItem) => void;
  onSearch?: (results: NewsItem[]) => void;
  selectedCategory?: string;
  onCategoryChange?: (category: string) => void;
  settings?: SiteSettings;
  businesses?: Business[];
  onNavigateGeneral?: (page: string) => void;
}

const AdvancedSearch: React.FC<AdvancedSearchProps> = ({ 
    items, 
    isOpen,
    onClose,
    onNavigate,
    onSearch,
    selectedCategory: propSelectedCategory,
    onCategoryChange,
    settings,
    businesses,
    onNavigateGeneral
}) => {
  const [query, setQuery] = useState('');
  const [internalSelectedCategory, setInternalSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('relevance');
  const [results, setResults] = useState<NewsItem[]>([]);
  const [searchInDirectory, setSearchInDirectory] = useState(false);
  
  const activeCategory = propSelectedCategory !== undefined ? propSelectedCategory : internalSelectedCategory;

  const modalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const isModal = isOpen !== undefined; 

  useEffect(() => {
    if (!isModal || !onClose) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose, isModal]);

  useEffect(() => {
    if (isModal) {
        if (isOpen) {
            setTimeout(() => inputRef.current?.focus(), 100);
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen, isModal]);

  const availableCategories = useMemo(() => {
      const cats = Array.from(new Set(items.map(i => i.category)));
      return cats.filter(c => c);
  }, [items]);

  useEffect(() => {
    let filtered = [...items].filter(n => {
      const isLocalSeoFlag = n.is_local_seo === true || (n.is_local_seo as any) === 1 || (n.is_local_seo as any) === '1';
      const hasLocalSeoTag = n.tags?.some(tag => {
        const t = tag.toLowerCase().trim();
        return t === 'local-seo' || t === 'seo-local' || t === 'seo local' || t === 'seo_local' || t === 'cluster' || t === 'pilar';
      });
      const isLocalSeoCategory = n.category?.toLowerCase().trim() === 'seo local' || n.category?.toLowerCase().trim() === 'local seo';
      
      // Show if not draft AND NOT local SEO
      return n.status !== 'draft' && !isLocalSeoFlag && !hasLocalSeoTag && !isLocalSeoCategory;
    });

    if (activeCategory !== 'all' && activeCategory !== 'Todas') { 
      filtered = filtered.filter(i => i.category === activeCategory);
    }

    if (query.trim()) {
      const q = query.toLowerCase();
      filtered = filtered.filter(i => 
        i.title.toLowerCase().includes(q) || 
        i.excerpt.toLowerCase().includes(q) ||
        i.category.toLowerCase().includes(q)
      );
    }

    if (sortBy === 'views_desc') {
        filtered.sort((a, b) => (b.views || 0) - (a.views || 0));
    } else if (sortBy === 'date_desc') {
        filtered.sort((a, b) => b.id.localeCompare(a.id));
    }

    if (onSearch) {
        onSearch(filtered);
    }

    setResults(filtered.slice(0, 8)); 
  }, [query, activeCategory, sortBy, items, onSearch]);

  const filteredBusinesses = useMemo(() => {
    if (!settings?.enableDirectory || !searchInDirectory || !businesses) {
      return [];
    }
    
    let filtered = [...businesses];
    
    // filter out drafts
    filtered = filtered.filter(b => b.status === 'published');
    
    if (query.trim()) {
      const q = query.toLowerCase();
      filtered = filtered.filter(b => 
        b.name.toLowerCase().includes(q) ||
        (b.description || '').toLowerCase().includes(q) ||
        (b.category || '').toLowerCase().includes(q) ||
        (b.address || '').toLowerCase().includes(q) ||
        (b.city || '').toLowerCase().includes(q) ||
        (b.keywords || '').toLowerCase().includes(q)
      );
    }
    
    // Sort featured first, then by name
    filtered.sort((a, b) => {
      if (a.featured && !b.featured) return -1;
      if (!a.featured && b.featured) return 1;
      return a.name.localeCompare(b.name);
    });
    
    return filtered.slice(0, 6);
  }, [query, searchInDirectory, businesses, settings]);

  const handleCategoryChange = (cat: string) => {
      if (onCategoryChange) {
          onCategoryChange(cat);
      } else {
          setInternalSelectedCategory(cat);
      }
  };

  if (isModal && !isOpen) return null;

  const containerClass = isModal 
    ? "fixed inset-0 z-[9999] flex items-start md:items-center justify-center p-3 md:p-6 pt-24 md:pt-6 bg-slate-950/40 backdrop-blur-md animate-fade-in overflow-hidden"
    : "w-full mb-8";

  const wrapperClass = isModal
    ? "bg-white w-full max-w-4xl rounded-2xl shadow-[0_32px_64px_-16px_rgba(0,0,0,0.15)] overflow-hidden flex flex-col animate-fade-in-up max-h-[85vh] md:max-h-[90vh] relative border border-gray-100"
    : "bg-white w-full rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col";

  const themeColor = settings?.primaryColor || '#2563eb';

  return (
    <div className={containerClass}>
      <div 
        ref={modalRef}
        className={wrapperClass}
      >
        {/* BOTÃO DE FECHAR (X) */}
        {isModal && onClose && (
            <button 
                onClick={onClose}
                className="absolute top-4 right-4 md:top-6 md:right-6 z-30 p-2 text-gray-400 hover:bg-gray-100 hover:text-red-500 rounded-lg transition-all active:scale-90 group"
                title="Fechar Busca"
            >
                <X size={18} className="transition-transform group-hover:rotate-90" />
            </button>
        )}

        {/* CABEÇALHO COM CAMPO DE BUSCA */}
        <div className="p-5 md:p-8 pb-4 md:pb-2 shrink-0">
            <div className="flex items-center justify-between mb-4">
                <span className="text-[10px] font-black uppercase tracking-[0.2em] ml-1 text-primary" style={{ color: themeColor }}>
                    {isModal ? "Explore o Conhecimento" : "O que você busca?"}
                </span>
            </div>
            <div className="relative flex items-center bg-gray-50 border border-gray-100 rounded-xl p-1 px-4 md:px-6 shadow-inner focus-within:bg-white focus-within:border-primary/20 focus-within:shadow-md transition-all group">
                <Search size={18} className="text-gray-300 group-focus-within:text-primary transition-colors" style={{ color: query ? themeColor : undefined }} />
                <input 
                    ref={inputRef}
                    type="text" 
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Busque por temas, ferramentas ou serviços..."
                    className="flex-1 px-3 md:px-4 py-3 md:py-3.5 text-sm md:text-base font-bold text-gray-800 outline-none bg-transparent placeholder:text-gray-300"
                />
                {isModal && (
                    <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg border border-gray-100 text-[9px] font-black text-gray-400 uppercase tracking-widest">
                        <Filter size={12} /> Filtros
                    </div>
                )}
            </div>

            {/* TOGGLE DO GUIA COMERCIAL */}
            {settings?.enableDirectory && (
                <div className="mt-3 flex items-center justify-between bg-slate-50 border border-slate-100/80 rounded-xl p-2.5 px-3.5 transition-all">
                    <div className="flex items-center gap-2.5 min-w-0">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ color: themeColor, backgroundColor: `${themeColor}15` }}>
                            <Store size={15} />
                        </div>
                        <div className="min-w-0">
                            <p className="text-[11px] font-black text-gray-800 leading-none mb-0.5">Pesquisar também no Guia Comercial</p>
                            <p className="text-[9px] text-gray-400 font-semibold truncate">Buscar empresas, comércios, profissionais e serviços locais</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => setSearchInDirectory(!searchInDirectory)}
                        className={`w-9 h-5 flex items-center rounded-full p-0.5 transition-all duration-300 relative focus:outline-none shrink-0 ${searchInDirectory ? 'bg-primary' : 'bg-gray-200'}`}
                        style={{ backgroundColor: searchInDirectory ? themeColor : undefined }}
                    >
                        <div className={`bg-white w-4 h-4 rounded-full shadow-sm transform transition-all duration-300 ${searchInDirectory ? 'translate-x-4' : 'translate-x-0'}`} />
                    </button>
                </div>
            )}
        </div>

        {/* CONTEÚDO EM DUAS COLUNAS */}
        <div className={`flex flex-col md:flex-row flex-1 overflow-hidden ${!isModal ? 'min-h-[400px]' : ''}`}>
            
            {/* COLUNA: FILTROS */}
            <aside className="w-full md:w-[200px] p-5 md:p-8 pt-2 md:pt-8 border-b md:border-b-0 md:border-r border-gray-50 flex flex-col gap-6 shrink-0 overflow-y-auto no-scrollbar md:max-h-none max-h-[120px]">
                <div>
                    <h4 className="flex items-center gap-2 text-[9px] font-black text-gray-400 uppercase tracking-[0.2em] mb-3">
                        <Tag size={10} className="text-primary/50" style={{ color: themeColor }} /> Categorias
                    </h4>
                    <div className="flex flex-row md:flex-wrap gap-2 overflow-x-auto md:overflow-visible pb-2 md:pb-0 no-scrollbar">
                        <button 
                            onClick={() => handleCategoryChange('all')}
                            className={`px-3 py-1.5 rounded-lg text-[9px] font-black transition-all border whitespace-nowrap uppercase tracking-wider ${activeCategory === 'all' || activeCategory === 'Todas' ? 'text-white border-primary shadow-sm' : 'bg-gray-50 text-gray-400 border-gray-100 hover:bg-gray-100'}`}
                            style={{ 
                              backgroundColor: activeCategory === 'all' || activeCategory === 'Todas' ? themeColor : undefined,
                              borderColor: activeCategory === 'all' || activeCategory === 'Todas' ? themeColor : undefined
                            }}
                        >
                            Todas
                        </button>
                        {availableCategories.map(cat => (
                            <button 
                                key={cat}
                                onClick={() => handleCategoryChange(cat)}
                                className={`px-3 py-1.5 rounded-lg text-[9px] font-black transition-all border whitespace-nowrap uppercase tracking-wider ${activeCategory === cat ? 'text-white border-primary shadow-sm' : 'bg-white text-gray-400 border-gray-100 hover:border-gray-200'}`}
                                style={{ 
                                  backgroundColor: activeCategory === cat ? themeColor : undefined,
                                  borderColor: activeCategory === cat ? themeColor : undefined
                                }}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="hidden md:block">
                    <h4 className="flex items-center gap-2 text-[9px] font-black text-gray-400 uppercase tracking-[0.2em] mb-3">
                        <TrendingUp size={10} className="text-primary/50" style={{ color: themeColor }} /> Ordenação
                    </h4>
                    <div className="relative">
                        <select 
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value as any)}
                            className="w-full bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 text-[10px] font-black text-gray-600 outline-none appearance-none cursor-pointer focus:border-primary/30 transition-all uppercase tracking-widest"
                        >
                            <option value="relevance">Relevância</option>
                            <option value="views_desc">Mais Vistos</option>
                            <option value="date_desc">Mais Novos</option>
                        </select>
                        <ChevronDown size={12} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    </div>
                </div>
            </aside>

            {/* COLUNA: RESULTADOS */}
            <main className="flex-1 p-5 md:p-8 overflow-y-auto custom-scrollbar bg-gray-50/10">
                
                {/* SEÇÃO GUIA COMERCIAL */}
                {searchInDirectory && settings?.enableDirectory && (
                    <div className="mb-6 animate-fade-in">
                        <div className="flex items-center justify-between mb-3 border-b border-gray-100 pb-2">
                            <h4 className="text-[10px] font-black uppercase tracking-[0.2em] flex items-center gap-1.5" style={{ color: themeColor }}>
                                <Briefcase size={12} /> Empresas no Guia
                            </h4>
                            <span className="text-[9px] font-extrabold text-gray-400">{filteredBusinesses.length} encontradas</span>
                        </div>
                        
                        {filteredBusinesses.length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {filteredBusinesses.map(bus => (
                                    <div 
                                        key={bus.id}
                                        onClick={() => {
                                            if (onNavigateGeneral) {
                                                const catSlug = (bus.category || 'geral').toLowerCase().replace(/\s+/g, '-');
                                                onNavigateGeneral(`guia/${catSlug}/${bus.slug || bus.id}`);
                                                if (onClose) onClose();
                                            }
                                        }}
                                        className="group flex items-center gap-3.5 p-3 rounded-xl bg-white border border-slate-100 hover:border-primary/10 hover:shadow-md transition-all cursor-pointer shadow-sm"
                                    >
                                        <div className="w-11 h-11 rounded-lg overflow-hidden bg-slate-50 shrink-0 border border-slate-100 flex items-center justify-center relative">
                                            {bus.imageUrl ? (
                                                <img src={bus.imageUrl} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" referrerPolicy="no-referrer" />
                                            ) : (
                                                <Briefcase size={16} className="text-gray-300 animate-pulse" />
                                            )}
                                            {bus.featured && (
                                                <span className="absolute top-0 right-0 bg-amber-500 text-white text-[6px] font-black uppercase px-1 py-0.5 rounded-bl-md">
                                                    Destaque
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h5 className="text-[11px] font-black text-gray-900 group-hover:text-primary transition-colors leading-tight mb-0.5 truncate group-hover:text-primary" style={{ '--hover-color': themeColor } as any}>
                                                {bus.name}
                                            </h5>
                                            <p className="text-[9px] text-gray-400 truncate mb-1 leading-none">
                                                {bus.description || 'Nenhuma descrição disponível'}
                                            </p>
                                            <div className="flex items-center gap-1.5">
                                                <span className="text-[8px] font-extrabold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded uppercase tracking-wider">
                                                    {bus.category || 'Geral'}
                                                </span>
                                                {bus.city && (
                                                    <span className="text-[8px] text-gray-400 font-bold truncate max-w-[90px]">
                                                        {bus.city}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="w-7 h-7 rounded-lg bg-slate-50 text-slate-300 flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-all shrink-0 group-hover:scale-105" style={{ '--hover-bg': themeColor } as any}>
                                            <ArrowRight size={12} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="py-6 flex flex-col items-center justify-center text-center bg-white rounded-xl border border-dashed border-slate-200">
                                <p className="font-black uppercase tracking-widest text-[8px] text-gray-400">Nenhuma empresa encontrada</p>
                                <p className="text-[9px] text-gray-300 mt-0.5">Tente buscar por termos diferentes.</p>
                            </div>
                        )}
                    </div>
                )}

                {/* SEÇÃO CONTEÚDOS & ARTIGOS */}
                <div className={searchInDirectory && settings?.enableDirectory ? "mt-4" : ""}>
                    <div className="flex items-center justify-between mb-4">
                        <h4 className="text-[9px] font-black text-gray-300 uppercase tracking-[0.2em]">
                            {searchInDirectory ? 'Artigos & Conteúdos' : (query ? 'Resultados' : 'Mais Buscados')}
                        </h4>
                        <span className="text-[9px] font-bold text-gray-300">{results.length} sugestões</span>
                    </div>

                    {results.length > 0 ? (
                        <div className="grid grid-cols-1 gap-3">
                            {results.map(item => (
                                <div 
                                    key={item.id}
                                    onClick={() => onNavigate?.(item)}
                                    className="group flex items-center gap-4 p-3 rounded-xl bg-white border border-transparent hover:border-primary/10 hover:shadow-md transition-all cursor-pointer shadow-sm"
                                >
                                    <div className="w-12 h-12 md:w-16 md:h-16 rounded-lg overflow-hidden bg-gray-100 shrink-0 border border-gray-50">
                                        <img src={item.imageUrl} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" referrerPolicy="no-referrer" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h5 className="text-xs md:text-sm font-black text-gray-900 group-hover:text-primary transition-colors leading-tight mb-1 line-clamp-1 group-hover:text-primary" style={{ '--hover-color': themeColor } as any}>
                                            {item.title}
                                        </h5>
                                        <div className="flex items-center gap-3">
                                            <span className="text-[8px] font-black px-1.5 py-0.5 rounded-md uppercase tracking-wider border" style={{ color: themeColor, backgroundColor: `${themeColor}08`, borderColor: `${themeColor}15` }}>
                                                {item.category}
                                            </span>
                                            <span className="flex items-center gap-1 text-[8px] text-gray-400 font-bold">
                                                <Clock size={8} /> {item.date}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="hidden md:flex w-8 h-8 rounded-lg bg-gray-50 text-gray-300 items-center justify-center group-hover:bg-primary group-hover:text-white transition-all transform group-hover:translate-x-1" style={{ '--hover-bg': themeColor } as any}>
                                        <ArrowRight size={14} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="py-16 flex flex-col items-center justify-center text-center">
                            <div className="w-12 h-12 bg-gray-50 rounded-xl flex items-center justify-center text-gray-200 mb-3">
                                <Search size={24} />
                            </div>
                            <p className="font-black uppercase tracking-widest text-[9px] text-gray-400">Nenhum resultado encontrado</p>
                            <p className="text-[10px] text-gray-300 mt-1">Tente palavras-chave mais genéricas.</p>
                        </div>
                    )}
                </div>
            </main>
        </div>

        {/* RODAPÉ DO MODAL */}
        {isModal && (
            <div className="bg-gray-50/50 px-6 md:px-8 py-3.5 border-t border-gray-100 flex justify-between items-center text-[8px] font-black text-gray-400 uppercase tracking-[0.2em] shrink-0">
                <div className="flex items-center gap-2">
                    <Zap size={10} className="text-primary/40" style={{ color: `${themeColor}60` }} />
                    <span>Algoritmo v2.0 Smart-Search</span>
                </div>
                <div className="hidden md:flex items-center gap-4">
                    <span className="opacity-50">ESC para fechar</span>
                </div>
            </div>
        )}
      </div>
    </div>
  );
};

export default AdvancedSearch;
